/**
 * 学習計画一覧ページ
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { LearningPlanService } from '../services/learningPlanService'
import { LearningPlanResponse, PlanStatus, PLAN_STATUS_COLORS } from '../types/learningPlan'
import LearningPlanCreateModal from '../components/learningPlan/LearningPlanCreateModal'
import { SearchField } from '../components/common/SearchField'

const LearningPlanPage: React.FC = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('learningPlan')
  const [plans, setPlans] = useState<LearningPlanResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<PlanStatus | ''>('')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedPlan, setSelectedPlan] = useState<LearningPlanResponse | null>(null)

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await LearningPlanService.getPlans({
        page,
        limit: 20,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
      })
      setPlans(response.plans)
      setTotalPages(response.pagination.pages)
    } catch (err) {
      console.error('プラン一覧取得エラー:', err)
      setError(t('messages.loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, searchTerm, statusFilter, t])

  useEffect(() => {
    loadPlans()
  }, [loadPlans])

  const handleSearch = () => {
    setPage(1)
    loadPlans()
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, plan: LearningPlanResponse) => {
    setAnchorEl(event.currentTarget)
    setSelectedPlan(plan)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedPlan(null)
  }

  const handleActivate = async () => {
    if (!selectedPlan) return
    try {
      await LearningPlanService.activatePlan(selectedPlan.id)
      setSuccessMessage(t('messages.activateSuccess'))
      handleMenuClose()
      loadPlans()
    } catch (err) {
      console.error('有効化エラー:', err)
      setError(t('messages.activateError'))
    }
  }

  const handlePause = async () => {
    if (!selectedPlan) return
    try {
      await LearningPlanService.pausePlan(selectedPlan.id)
      setSuccessMessage(t('messages.pauseSuccess'))
      handleMenuClose()
      loadPlans()
    } catch (err) {
      console.error('一時停止エラー:', err)
      setError(t('messages.pauseError'))
    }
  }

  const handleCancel = async () => {
    if (!selectedPlan) return
    if (!window.confirm(t('messages.cancelConfirm'))) return
    try {
      await LearningPlanService.cancelPlan(selectedPlan.id)
      setSuccessMessage(t('messages.cancelSuccess'))
      handleMenuClose()
      loadPlans()
    } catch (err) {
      console.error('キャンセルエラー:', err)
      setError(t('messages.cancelError'))
    }
  }

  const handleDelete = async () => {
    if (!selectedPlan) return
    if (!window.confirm(t('messages.deleteConfirm'))) return
    try {
      await LearningPlanService.deletePlan(selectedPlan.id)
      setSuccessMessage(t('messages.deleteSuccess'))
      handleMenuClose()
      loadPlans()
    } catch (err) {
      console.error('削除エラー:', err)
      setError(t('messages.deleteError'))
    }
  }

  const handleCreateSuccess = (plan: LearningPlanResponse) => {
    setSuccessMessage(t('messages.createSuccess'))
    setCreateModalOpen(false)
    loadPlans()
  }

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale)
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          {t('createNew')}
        </Button>
      </Box>

      {/* メッセージ */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* 検索・フィルタ */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
            placeholder={t('searchPlaceholder')}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('columns.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('columns.status')}
              onChange={(e) => {
                setStatusFilter(e.target.value as PlanStatus | '')
                setPage(1)
              }}
            >
              <MenuItem value="">{t('status.all')}</MenuItem>
              <MenuItem value="DRAFT">{t('status.DRAFT')}</MenuItem>
              <MenuItem value="ACTIVE">{t('status.ACTIVE')}</MenuItem>
              <MenuItem value="PAUSED">{t('status.PAUSED')}</MenuItem>
              <MenuItem value="COMPLETED">{t('status.COMPLETED')}</MenuItem>
              <MenuItem value="CANCELLED">{t('status.CANCELLED')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* テーブル */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : plans.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">{t('list.noPlans')}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>{t('columns.planName')}</TableCell>
                  <TableCell>{t('columns.complianceRule')}</TableCell>
                  <TableCell>{t('columns.targetGroup')}</TableCell>
                  <TableCell>{t('columns.frequency')}</TableCell>
                  <TableCell>{t('columns.period')}</TableCell>
                  <TableCell>{t('columns.status')}</TableCell>
                  <TableCell>{t('columns.progress')}</TableCell>
                  <TableCell align="right">{t('columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow
                    key={plan.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/learning-plans/${plan.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {plan.title}
                      </Typography>
                      {plan.description && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          sx={{ maxWidth: 200, display: 'block' }}
                        >
                          {plan.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={plan.complianceRule?.category || ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {plan.complianceRule?.title || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{plan.userGroup?.name || '-'}</Typography>
                      {plan.userGroup?.memberCount !== undefined && (
                        <Typography variant="caption" color="textSecondary">
                          {t('memberCount', { count: plan.userGroup.memberCount })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {t(`frequency.${plan.frequency}`)}
                      {plan.frequency === 'CUSTOM' && plan.customIntervalDays && (
                        <Typography variant="caption" display="block">
                          {t('frequency.customDays', { days: plan.customIntervalDays })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(plan.startDate)}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        〜 {formatDate(plan.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`status.${plan.status}`)}
                        color={PLAN_STATUS_COLORS[plan.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {plan.scheduledTestsCount !== undefined && (
                        <Typography variant="body2">
                          {plan.completedTestsCount || 0} / {plan.scheduledTestsCount}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => handleMenuClick(e, plan)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ページネーション */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* アクションメニュー */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            navigate(`/learning-plans/${selectedPlan?.id}`)
            handleMenuClose()
          }}
        >
          <ViewIcon sx={{ mr: 1 }} fontSize="small" /> {t('actions.viewDetails')}
        </MenuItem>
        {selectedPlan?.status === 'DRAFT' && (
          <MenuItem
            onClick={() => {
              navigate(`/learning-plans/${selectedPlan?.id}/edit`)
              handleMenuClose()
            }}
          >
            <EditIcon sx={{ mr: 1 }} fontSize="small" /> {t('actions.edit')}
          </MenuItem>
        )}
        {(selectedPlan?.status === 'DRAFT' || selectedPlan?.status === 'PAUSED') && (
          <MenuItem onClick={handleActivate}>
            <PlayIcon sx={{ mr: 1 }} fontSize="small" /> {t('actions.activate')}
          </MenuItem>
        )}
        {selectedPlan?.status === 'ACTIVE' && (
          <MenuItem onClick={handlePause}>
            <PauseIcon sx={{ mr: 1 }} fontSize="small" /> {t('actions.pause')}
          </MenuItem>
        )}
        {(selectedPlan?.status === 'DRAFT' ||
          selectedPlan?.status === 'ACTIVE' ||
          selectedPlan?.status === 'PAUSED') && (
          <MenuItem onClick={handleCancel} sx={{ color: 'secondary.main' }}>
            <CancelIcon sx={{ mr: 1 }} fontSize="small" /> {t('actions.cancel')}
          </MenuItem>
        )}
        {(selectedPlan?.status === 'DRAFT' || selectedPlan?.status === 'CANCELLED') && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> {t('actions.delete')}
          </MenuItem>
        )}
      </Menu>

      {/* 作成モーダル */}
      <LearningPlanCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Box>
  )
}

export default LearningPlanPage
